'use client';

// Composition of the three realtime providers — RealtimeProvider for raw
// subscriber access (legacy hooks), SnapshotProvider for current-state
// subscriptions, and LedgerProvider for append-only subscriptions. All
// three share ONE subscriber instance so the underlying transport stays
// single-channel-per-topic regardless of which provider opened the
// subscription.
//
// Lives in `@auctogon/core/realtime` so admin-web and bidder-web mount
// identical context shape — per `sharing.md` Rule 18.

import { type ReactNode } from 'react';

import type { ResponseStatusHandlers } from '../../api/response-status-handlers';
import { ConnectionHealthProvider } from '../connection-health';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { LedgerProvider } from './LedgerContext';
import { RealtimeProvider } from './RealtimeProvider';
import { SnapshotProvider } from './SnapshotContext';

export interface RealtimeStackProps {
    /** Transport subscriber every provider in the stack shares. */
    subscriber: RealtimeSubscriber;
    /**
     * Override the LedgerProvider's periodic reconcile interval. Tests pass
     * `0` to disable; production code should accept the default (2 min per
     * BL-563).
     */
    ledgerReconcileIntervalMs?: number;
    /**
     * Override the SnapshotProvider's periodic reconcile interval. Tests
     * pass `0` to disable; production code should accept the default (2 min
     * per BL-563).
     */
    snapshotReconcileIntervalMs?: number;
    /**
     * Override the LedgerProvider's reconcile throttle window (BUG-1175).
     * Tests that compress the interval below the throttle window pass `0` to
     * isolate interval wiring; production accepts the default.
     */
    ledgerReconcileThrottleMs?: number;
    /**
     * Override the SnapshotProvider's reconcile throttle window (BUG-1175).
     * Tests that compress the interval below the throttle window pass `0` to
     * isolate interval wiring; production accepts the default.
     */
    snapshotReconcileThrottleMs?: number;
    /**
     * App-injected 401/418 handlers forwarded to the SnapshotProvider's default
     * catch-up fetcher — routes a banned (418) / stale-session (401) catch-up
     * to the account-unavailable / login screen (BUG-1015 AC 3). Omit for no
     * routing (admin-web today).
     */
    responseStatusHandlers?: ResponseStatusHandlers;
    /**
     * Snapshot kinds whose app exposes a `/<kind>/catchup/batch` endpoint
     * (BL-1465) — forwarded to the SnapshotProvider, which coalesces same-kind
     * catch-ups issued in one task into a single batched POST. Per-kind opt-in;
     * omit for the original one-request-per-topic behaviour (admin-web today).
     * MUST be a stable reference — see `SnapshotProviderProps.batchableKinds`.
     */
    snapshotBatchableKinds?: ReadonlySet<string>;
    /**
     * Endpoint the connection-health reachability probe hits. Defaults to
     * `/api/time`, which both apps expose publicly and DB-free. Tests that
     * assert on probe traffic can point it elsewhere.
     */
    connectionHealthProbeUrl?: string;
    children: ReactNode;
}

/**
 * Mount once per app, typically inside the root layout. Provides
 * `useRealtimeSubscriber`, `useSnapshot`, and `useLedger` to everything
 * underneath.
 */
export function RealtimeStack({
    subscriber,
    ledgerReconcileIntervalMs,
    snapshotReconcileIntervalMs,
    ledgerReconcileThrottleMs,
    snapshotReconcileThrottleMs,
    responseStatusHandlers,
    snapshotBatchableKinds,
    connectionHealthProbeUrl,
    children,
}: RealtimeStackProps) {
    return (
        <RealtimeProvider subscriber={subscriber}>
            <SnapshotProvider
                subscriber={subscriber}
                reconcileIntervalMs={snapshotReconcileIntervalMs}
                reconcileThrottleMs={snapshotReconcileThrottleMs}
                responseStatusHandlers={responseStatusHandlers}
                batchableKinds={snapshotBatchableKinds}
            >
                <LedgerProvider
                    subscriber={subscriber}
                    reconcileIntervalMs={ledgerReconcileIntervalMs}
                    reconcileThrottleMs={ledgerReconcileThrottleMs}
                >
                    {/* BL-1090 — one app-wide connection-health monitor per
                        app, hung off the same shared subscriber the rest of
                        the stack uses. Innermost so it can't re-render the
                        realtime providers when its state flips. */}
                    <ConnectionHealthProvider
                        subscriber={subscriber}
                        probeUrl={connectionHealthProbeUrl}
                    >
                        {children}
                    </ConnectionHealthProvider>
                </LedgerProvider>
            </SnapshotProvider>
        </RealtimeProvider>
    );
}
